<template>
  <v-container fluid class="virtualize-table-container" ref="container">
    <v-row>
      <v-col xs="12">
        <div
          class="table-header"
          :style="{ gridTemplateColumns: gridTemplateStyles }"
        >
          <div
            class="table-header__column"
            :style="getStyles(header)"
            v-for="header of headers"
            :key="header.value"
          >
            <p class="table-header__text subtitle-2">{{ header.text }}</p>
          </div>
        </div>
        <v-progress-linear indeterminate :active="loading" color="primary" />
        <template v-if="!rows.length">
          <v-row>
            <v-col cols="12">
              <p class="body-1 text-center pb-0 pt-4">
                {{ loading ? 'Loading...' : 'No data available' }}
              </p>
            </v-col>
          </v-row>
          <v-divider />
        </template>
        <v-virtual-scroll
          bench="3"
          item-height="68"
          :items="rows"
          :height="parentHeight"
        >
          <template v-slot:default="{ item }">
            <div
              class="table-row"
              :style="{ gridTemplateColumns: gridTemplateStyles }"
              v-if="!item.isGroupKey"
            >
              <div
                class="table-cell"
                v-for="header of headers"
                :key="header.value + item.id"
                :style="getStyles(header)"
              >
                <slot :name="header.value" v-bind:item="item">
                  <span class="body-2">{{ $v(item, header.value) }}</span>
                </slot>
              </div>
            </div>
            <template v-else>
              <template v-if="groupByHeaders.length">
                <div class="table-row group-header with-headers">
                  <div class="table-row table-header-row">
                    <div
                      class="table-cell"
                      v-for="header of groupByHeaders"
                      :key="header.value + item.id"
                      :style="getStyles(header)"
                    >
                      <span class="caption font-weight-bold">
                        {{ header.text }}
                      </span>
                    </div>
                  </div>
                </div>
                <div class="table-row group-header with-headers">
                  <div class="table-row table-header-row">
                    <div
                      class="table-cell"
                      v-for="header of groupByHeaders"
                      :key="header.value + item.id"
                    >
                      <v-tooltip bottom max-width="300">
                        <template v-slot:activator="{ on }">
                          <span class="caption" v-on="on">
                            {{ $v(item, ['groupRow', header.value]) }}
                          </span>
                        </template>
                        <span>{{ $v(item, ['groupRow', header.value]) }}</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
              </template>
              <div class="table-row group-header" v-else>
                <span class="body-2">{{ groupTitlePrefix + item.key }}</span>
              </div>
            </template>
          </template>
        </v-virtual-scroll>
      </v-col>
    </v-row>
    <v-row v-if="showPagination && rows.length">
      <v-spacer />
      <template v-if="showRowsPerPageSelector">
        <v-col cols="auto" class="pr-0">
          <v-subheader>Rows per page:</v-subheader>
        </v-col>
        <v-col cols="auto" class="pt-5 pl-0">
          <v-select
            dense
            single-line
            class="items-per-page-select"
            :items="itemsPerPageOptions"
            :value="itemsPerPage"
            label="Items per page"
            @change="handleItemsPerPageChange"
          />
        </v-col>
      </template>
      <v-col cols="auto" v-if="!isAllSelected">
        <v-pagination
          total-visible="5"
          :length="pagination.pageCount"
          :value="pagination.page"
          @next="handleNextPage"
          @previous="handlePrevPage"
          @input="handlePageChange"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import _ from 'lodash';

const ALL_OPTION_NAME = 'All';

export default {
  props: {
    items: {
      type: Array,
      default: () => []
    },
    headers: {
      type: Array,
      default: () => []
    },
    groupBy: {
      type: String,
      default: ''
    },
    groupTitlePrefix: {
      type: String,
      default: ''
    },
    groupByHeaders: {
      type: Array,
      default: () => []
    },
    showPagination: {
      type: Boolean,
      default: false
    },
    showRowsPerPageSelector: {
      type: Boolean,
      default: true
    },
    pagination: {
      type: Object,
      default: () => ({
        page: 1,
        pageCount: 2,
        pageSize: 20,
        rowCount: 0
      })
    },
    itemsPerPage: {
      type: Number,
      default: 200
    },
    showAllOption: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      parentHeight: null,
      gridTemplateStyles: 'repeat(auto-fit, minmax(100px, 1fr))',
      groupByItemsMap: {},
      rows: [],
      itemsPerPageOptionsList: [50, 100, 200, 500]
    };
  },
  computed: {
    itemsPerPageOptions() {
      return this.showAllOption
        ? [...this.itemsPerPageOptionsList, ALL_OPTION_NAME]
        : this.itemsPerPageOptionsList;
    },
    isAllSelected() {
      return this.pagination.pageSize === ALL_OPTION_NAME;
    }
  },
  mounted() {
    this.getGridTemplateStyles();
  },
  updated() {
    if (!this.parentHeight) {
      this.parentHeight = this.showPagination
        ? this.$refs.container.clientHeight - 159
        : this.$refs.container.clientHeight;
    }
  },
  watch: {
    items(newItems) {
      if (this.groupBy) {
        const groupByItemsMap = _.groupBy(newItems, this.groupBy);
        this.rows = Object.keys(groupByItemsMap).reduce(
          (rows, groupKey) => [
            ...rows,
            {
              isGroupKey: true,
              key: groupKey,
              groupRow: _.head(groupByItemsMap[groupKey])
            },
            ...groupByItemsMap[groupKey]
          ],
          []
        );
      } else {
        this.rows = newItems;
      }

      this.getGridTemplateStyles();
    }
  },
  methods: {
    getGridTemplateStyles() {
      this.gridTemplateStyles = this.headers
        .map(({ width }) => (width ? `${width}px` : '1fr'))
        .join(' ');
    },
    getStyles({ align, justify, width }) {
      const styles = {};

      if (align) {
        styles['align-items'] = align;
      }
      if (justify) {
        styles['justify-content'] = justify;
      }
      if (width) {
        styles['width'] = `${width}px`;
      }

      return styles;
    },
    handleItemsPerPageChange(value) {
      this.$emit('onChangeItemsPerPage', value);
    },
    handlePageChange(pageNum) {
      this.$emit('onChangePage', pageNum);
    },
    handleNextPage() {
      this.$emit('onChangeNextPage');
    },
    handlePrevPage() {
      this.$emit('onChangePrevPage');
    }
  }
};
</script>

<style lang="scss">
$tableRowHeight: 68px;

.table-header {
  display: grid;
  border-bottom: 1px solid rgb(0 0 0 / 12%);
  padding: 16px 16px 16px 0;

  .table-header__column {
    display: flex;
    padding-left: 16px;

    .table-header__text {
      margin: 0;
    }
  }
}

.table-row {
  display: grid;
  height: $tableRowHeight;
  padding: 0 16px 0 0;
  border-bottom: 1px solid rgb(0 0 0 / 12%);
  overflow: hidden;
  &.group-header {
    display: flex;
    align-items: center;
    padding: 16px;
    background-color: #eeeeee;
  }
  &.with-headers {
    display: block;
    height: calc(#{$tableRowHeight} / 2);
    padding: 0;
  }
  &.table-header-row {
    height: auto;
    max-height: 100%;
    overflow: hidden;
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    background-color: #eeeeee;

    .table-cell {
      span {
        display: inline-block;
        white-space: nowrap;
      }
    }
  }
  &:not(.group-header),
  &:not(.table-header-row) {
    &:hover {
      background-color: #eeeeee;
    }
  }

  .table-cell {
    display: flex;
    padding: 8px 16px;
    border-left: 1px solid rgb(0 0 0 / 12%);
    overflow: hidden;
    &:first-of-type {
      border-left: 0;
    }

    span {
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.v-virtual-scroll__container {
  .v-virtual-scroll__item {
    &:nth-of-type(even) {
      background-color: #f5f9fb;
    }
  }
}

.items-per-page-select {
  max-width: 60px;
}

.virtualize-table-container {
  height: 100%;
  max-height: 100%;
  flex: 1 !important;
}
</style>
